import axios from 'axios'
import { handleErrorFetchingData } from '../../../functions/errorHandling'
import getServerURL from '../../../functions/getServerURL'

const defaultState = {
	sapResources: [],
	resourcesCount: 0,
	resourcesPerPage: 0,
	currentResourcesPage: 1,
	fetchingSAPResources: false,
	fetchingSAPResourcesMetadata: false,
	contents: [],
	dataSources: [],
	designs: [],
	methods: [],
	therapeuticAreas: [],

	// FILTERS
	sortBy: '-created_date',
	searchTerm: '',
	contentFilters: [],
	dataSourceFilters: [],
	designFilters: [],
	methodFilters: [],
	therapeuticAreaFilters: [],
	externalClientFilters: [],
	authorFilters: []
}

export default function manageSAPResources(state = defaultState, action) {
	switch (action.type) {
		case 'TOGGLE_FETCHING_SAP_RESOURCES':
			return { ...state, fetchingSAPResources: action.payload }
		case 'TOGGLE_FETCHING_SAP_RESOURCES_METADATA':
			return { ...state, fetchingSAPResourcesMetadata: action.payload }
		case 'SET_SAP_RESOURCES':
			return {
				...state,
				sapResources: action.payload.results,
				resourcesCount: action.payload.count,
				resourcesPerPage: action.payload.results_per_page,
			}
		case 'SET_SAP_RESOURCES_METADATA':
			return {
				...state,
				contents: action.payload.contents || [],
				dataSources: action.payload.data_sources || [],
				designs: action.payload.designs || [],
				methods: action.payload.methods || [],
				therapeuticAreas: action.payload.therapeutic_areas || [],
			}
		case 'SET_SAP_RESOURCES_CURRENT_PAGE':
			return { ...state, currentResourcesPage: action.payload }
		case 'HANDLE_CHANGE_SAP_FILTER':
			if (action.payload.name) {
				return {
					...state,
					[action.payload.name]: action.payload.value,
				}
			}
			return state
		case 'CLEAR_ALL_SAP_RESOURCE_FILTERS':
			return {
				...state,
				sortBy: '-created_date',
				searchTerm: '',
				contentFilters: [],
				dataSourceFilters: [],
				designFilters: [],
				methodFilters: [],
				therapeuticAreaFilters: [],
				authorFilters: []
			}	
		default:
			return state
	}
}

const toggleFetchingSAPResources = (bool) => {
	return { type: 'TOGGLE_FETCHING_SAP_RESOURCES', payload: bool }
}

const setSAPResources = (resources) => {
	return { type: 'SET_SAP_RESOURCES', payload: resources }
}

const setCurrentSAPResourcesPage = (page) => {
	return { type: 'SET_SAP_RESOURCES_CURRENT_PAGE', payload: page }
}

export const getSAPResources = (page = 1, filters, sort_by) => {
	return dispatch => {
		dispatch(toggleFetchingSAPResources(true))

		axios({
			method: 'GET',
			headers: { "Authorization": "Bearer " + localStorage.getItem("token") },
			url: `${getServerURL()}sap-resources/sap-resources/?page=${page}`,
			params: {
				filters,
				sort_by,
			}
		})
			.then(response => {
				dispatch(setCurrentSAPResourcesPage(page))
				dispatch(setSAPResources(response.data))
			})
			.catch(error => {
				handleErrorFetchingData(error)
			})
			.finally(() => {
				dispatch(toggleFetchingSAPResources(false))
			})
	}
}

const toggleFetchingSAPResourcesMetadata = (bool) => {
	return { type: 'TOGGLE_FETCHING_SAP_RESOURCES_METADATA', payload: bool }
}

const setSAPResourcesMetadata = (metadata) => {
	return { type: 'SET_SAP_RESOURCES_METADATA', payload: metadata }
}

export const getSAPResourcesMetadata = () => {
	return dispatch => {
		dispatch(toggleFetchingSAPResourcesMetadata(true))

		axios({
			method: 'GET',
			headers: { "Authorization": "Bearer " + localStorage.getItem("token") },
			url: `${getServerURL()}sap-resources/metadata`,
		})
			.then(response => {
				dispatch(setSAPResourcesMetadata(response.data))
			})
			.catch(error => {
				handleErrorFetchingData(error)
			})
			.finally(() => {
				dispatch(toggleFetchingSAPResourcesMetadata(false))
			})
	}
}

export const handleChangeSAPFilter = (name, value) => {
	return {
		type: 'HANDLE_CHANGE_SAP_FILTER',
		payload: { name, value },
	}
}

export const formatResourceForServer = (resource) => {
	const newResource = new FormData()

	newResource.append('title', resource.title)
	newResource.append('text', resource.text || '')
	newResource.append('project', resource.project || '')
	newResource.append('project_folder_path', resource.projectFolderPath || '')
	// newResource.append('authors', resource.authors || '')
	newResource.append('is_protocol', resource.isProtocol || false)
	newResource.append('is_sap', resource.isSAP || false)

	if (Array.isArray(resource.authors) && resource.authors.length) {
		const authorIds = resource.authors.map(author => author.id)
		newResource.append('authors', JSON.stringify(authorIds))
	}

	if (Array.isArray(resource.externalClient) && resource.externalClient[0]) {
		newResource.append('external_client', resource.externalClient[0].url)
	}

	resource.documents.forEach((doc, index) => {
		if (!doc.url) {
			newResource.append(`document_${index}`, doc)
		}
	})
	
	const contents = []
	const new_contents = []
	resource.contents.forEach(content => {
		if (content.url) {
			contents.push(content.id)
		} else {
			new_contents.push(content.name)
		}
	})
	
	if (contents.length) {
		newResource.append('contents', JSON.stringify(contents))
	}
	
	if (new_contents.length) {
		newResource.append('new_contents', JSON.stringify(new_contents))
	}

	const data_sources = []
	const new_data_sources = []
	resource.dataSources.forEach(dataSource => {
		if (dataSource.url) {
			data_sources.push(dataSource.id)
		} else {
			new_data_sources.push(dataSource.name)
		}
	})
	
	if (data_sources.length) {
		newResource.append('data_sources', JSON.stringify(data_sources))
	}
	
	if (new_data_sources.length) {
		newResource.append('new_data_sources', JSON.stringify(new_data_sources))
	}

	const designs = resource.designs.map(design => design.id)

	if (designs.length) {
		newResource.append('designs', JSON.stringify(designs))
	}

	const methods = []
	const new_methods = []
	resource.methods.forEach(method => {
		if (method.url) {
			methods.push(method.id)
		} else {
			new_methods.push(method.name)
		}
	})
	
	if (methods.length) {
		newResource.append('methods', JSON.stringify(methods))
	}
	
	if (new_methods.length) {
		newResource.append('new_methods', JSON.stringify(new_methods))
	}

	const therapeutic_areas = resource.therapeuticAreas.map(therapeuticArea => therapeuticArea.id)
	
	if (therapeutic_areas.length) {
		newResource.append('therapeutic_areas', JSON.stringify(therapeutic_areas))
	}

	return newResource
}



// WEBPACK FOOTER //
// ./src/routes/sap-resources/reducers/manageSAPResources.js